import Http from '@/services/http';
import {
  AlarmActionRequest,
  AlarmActionResponse,
  AlarmSummaryRequest,
  AlarmSummaryResponse,
  LastAlarmRequest,
  PagedLastAlarmResponse,
  InstallationMenuRequest,
  InstallationMenuResponse,
  NetworkStatusSummaryRequest,
  NetworkStatusSummaryResponse,
  DashobardPeriodFilterRequest,
  AlarmOverviewByAssetResponse,
  ActedAlarmsVsRecognizedAlarmsRequest,
  ActedAlarmsVsRecognizedAlarmsResponse,
  MostActedAlarmsResponse,
  MostActedAlarmsFilterRequest,
  AlarmOverviewByClassificationResponse,
} from 'sigma-mappings';

const urls = {
  getInstallationMenu: 'empresa/regional',
  getAlarmSummary: 'alarme/resumo',
  getAlarmsList: 'alarme/ultimos',
  getActedAlarmsVsRecognizedAlarms: 'alarme/atuados-vs-reconhecidos/grafico',
  getNetworkStatusSummary: 'alarme/statusrede',
  recognizeAlarm: alarmId => `alarme/${alarmId}/reconhecer`,
  ignoreAlarm: alarmId => `alarme/${alarmId}/ignorar`,
  alarmsOverviewByAsset: 'alarme/visao-geral-por-ativo/grafico',
  getMostActedAlarms: 'alarme/mais-atuados',
  generalViewByClassification: 'alarme/visao-geral-por-classificacao/grafico',
};

export default {
  /**
   * Retorna uma lista de instalações agrupadas por empresa e regional (se houver)
   * @param {*} search busca por nome da empresa ou da instalação
   */
  getInstallationMenuData(search) {
    const http = new Http();
    const params = new InstallationMenuRequest({
      search,
    });
    return http
      .post(urls.getInstallationMenu, params)
      .then(({ data }) => data.map(item => new InstallationMenuResponse(item)));
  },
  /**
   * Retorna um resumo dos alarmes, alarme atuados e alarmes não reconhecidos
   * @param {*} companiesIds lista de ids de empresas para filtro
   * @param {*} installationsIds lista de ids de instalações para filtro
   */
  getAlarmSummary(companiesIds, installationsIds) {
    const http = new Http();
    const params = new AlarmSummaryRequest({
      companiesIds,
      installationsIds,
    });
    return http
      .post(urls.getAlarmSummary, params)
      .then(({ data }) => new AlarmSummaryResponse(data));
  },
  /**
   * Recupera uma lista dos ultimos alarmes ordenada de forma decrescente, paginada.
   * @param {*} companyId id da empresa para filtro
   * @param {*} installationId id da instalação para filtro
   * @param {*} classifications lista de classificações para filtro
   * @param {*} status lista de status para filtro
   * @param {*} fromDate data e hora enviada para resgatar registros a partir dela
   * @param {*} lastReturnedIds Ids da última página que possuem essa data e hora
   * @param {*} pageSize Quantidade de alarmes a serem retornados
   */
  getAlarmsList({
    companyId,
    classifications,
    status,
    fromDate,
    lastReturnedIds,
    installationId,
    pageSize,
  }) {
    const http = new Http();
    const parameters = new LastAlarmRequest({
      companyId,
      classifications,
      status,
      fromDate,
      installationId,
      lastReturnedIds,
      pageSize,
    });
    return http
      .post(urls.getAlarmsList, parameters)
      .then(response => new PagedLastAlarmResponse(response.data));
  },
  /**
   * Retorna um resumo de status de rede, agrupando por status.
   * @param {*} companiesIds lista de ids de empresas para filtro
   * @param {*} installationsIds lista de ids de instalações para filtro
   */
  getNetworkStatusSummary(companiesIds, installationsIds) {
    const http = new Http();
    const parameters = new NetworkStatusSummaryRequest({
      companiesIds,
      installationsIds,
    });
    return http
      .post(urls.getNetworkStatusSummary, parameters)
      .then(({ data }) => new NetworkStatusSummaryResponse(data));
  },
  /**
   * Requisição para reconhecer alarme
   * @param {*} alarmId Id do Alarme
   * @param {*} description comentário opcional vinculado ao reconhecimento
   * @param {*} ignore Opção para ignorar alarme
   */
  recognizeAlarm({ alarmId, description, ignore = false }) {
    const http = new Http();
    const parameters = new AlarmActionRequest({
      description,
      ignore,
    });
    return http
      .post(urls.recognizeAlarm(alarmId), parameters)
      .then(response => new AlarmActionResponse(response.data));
  },
  /**
   * Ignora um alarme previamente reconhecido
   * @param {*} alarmId Id do Alarme
   * @param {*} description comentário opcional vinculado à ação de ignorar
   */
  ignoreAlarm({ alarmId, description }) {
    const http = new Http();
    const parameters = new AlarmActionRequest({
      description,
    });
    return http
      .post(urls.ignoreAlarm(alarmId), parameters)
      .then(({ data }) => data);
  },
  /**
   * Retorna os dados do gráfico de alarmes atuados vs alarmes reconhecidos.
   * @param {*} companiesIds lista de ids de empresas para filtro
   * @param {*} regionalsIds lista de ids de regionais para filtro
   * @param {*} installationsIds lista de ids de instalações para filtro
   * @param {*} period quantidade de dias anterior ao dia atual para filtro
   */
  getActedAlarmsVsRecognizedAlarms(params) {
    const http = new Http();
    const request = new ActedAlarmsVsRecognizedAlarmsRequest(params);
    return http
      .post(urls.getActedAlarmsVsRecognizedAlarms, request)
      .then(
        ({ data }) => (data || [])
          .map(
            item => new ActedAlarmsVsRecognizedAlarmsResponse(item),
          ),
      );
  },
  getAlarmsOverviewByAsset(filter) {
    const http = new Http();
    const payload = new DashobardPeriodFilterRequest(filter);
    return http.post(urls.alarmsOverviewByAsset, payload)
      .then(({ data }) => data.map(item => new AlarmOverviewByAssetResponse(item)));
  },

  getMostActedAlarms({
    companiesIds, regionalsIds, installationsIds, periodDays, amountAlarms,
  }) {
    const http = new Http();
    const parameters = new MostActedAlarmsFilterRequest({
      companiesIds,
      regionalsIds,
      installationsIds,
      periodDays,
      amountAlarms,
    });
    return http.post(urls.getMostActedAlarms, parameters)
      .then(({ data }) => data.map(item => new MostActedAlarmsResponse(item)));
  },

  getGeneralViewByClassification(filter) {
    const http = new Http();
    const payload = new DashobardPeriodFilterRequest(filter);
    return http.post(urls.generalViewByClassification, payload)
      .then(({ data }) => data.map(item => new AlarmOverviewByClassificationResponse(item)));
  },
};
